import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal, OnInit, Inject, WritableSignal } from '@angular/core';
import { IonApp, IonButton, IonIcon, IonRouterOutlet, IonSpinner } from '@ionic/angular';
import { MESSAGES } from './constants/messages.constants';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { EventType, InteractionStatus } from '@azure/msal-browser';
import { catchError, filter, forkJoin, of, Subject, takeUntil, timeout } from 'rxjs';
import { NavbarComponent } from './components/navbar/navbar.component';
import { LeftSideBarComponent } from './components/left-side-bar/left-side-bar.component';
import { addIcons } from 'ionicons';
import * as allIcons from 'ionicons/icons';
import { RouterOutlet } from '@angular/router';
import { CoreApiService } from './services/core-api.service';
import { LOADING_STATUS } from './types/app.types';
import { Store } from '@ngrx/store';
import { storeFilters } from './state/actions/filter.action';
import { SessionStorageService } from './services/session-storage.service';
import { setProfile } from './state/actions/app.actions';
import { EventBusService } from './services/event-bus.service';
import { UserService } from './services/user-roles.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.css'],
  imports: [CommonModule, IonApp, RouterOutlet, LeftSideBarComponent, NavbarComponent, IonButton, IonIcon, IonSpinner],
})
export class AppComponent implements OnInit {
  private authService = inject(MsalService);
  private coreApiService = inject(CoreApiService);
  private sessionStorageService = inject(SessionStorageService);
  private eventBusService = inject(EventBusService);
  readonly messages = MESSAGES;

  private readonly _destroying$ = new Subject<void>();
  private appInitializerWorker?: Worker;

  public error: any;

  appMainContainState: WritableSignal<{
    fadeInMain: boolean;
    hiddenMain: boolean;
  }> = signal({
    fadeInMain: false,
    hiddenMain: false,
  });

  appLoaderState: WritableSignal<{
    fadeOut: boolean;
    hidden: boolean;
    hasApiFailed: boolean;
  }> = signal({
    fadeOut: false,
    hidden: false,
    hasApiFailed: false,
  });

  appLoadingStatus: WritableSignal<LOADING_STATUS> = signal(LOADING_STATUS.NOT_STARTED);
  constructor(
    private msalBroadcastService: MsalBroadcastService,
    private store: Store,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private userService: UserService
  ) {
    addIcons(allIcons);
  }

  async ngOnInit() {
    this.msalInit();
    this.sessionStorageService.storage$.subscribe(async (change: any) => {
      if (this.sessionStorageService.getItem('idToken') && this.sessionStorageService.getItem('accessToken')) {
        await this.initializeAppData();
      }
    });
  }

  arrayBufferToBase64(buffer: ArrayBuffer): string {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)));
  }

  async initializeAppData(): Promise<void> {
    if ([LOADING_STATUS.NOT_STARTED, LOADING_STATUS.ERROR].includes(this.appLoadingStatus())) {
      this.appLoadingStatus.set(LOADING_STATUS.LOADING);
      const azureProfileApi = this.coreApiService.getAzureProfile().pipe(
        catchError((error) => {
          console.error('getAzureProfile', error);
          return of(null);
        })
      );
      const azureProfileImageApi = this.coreApiService.getUserAvatarImage().pipe(
        catchError((error) => {
          console.error('getUserAvatarImage', error);
          return of(null);
        })
      );

      const filterApi = await this.coreApiService.getFilters();
      forkJoin([azureProfileApi, azureProfileImageApi, filterApi, this.userService.getRoles()]).subscribe(
        ([azureProfile, azureProfileImage, filters]) => {
          this.appInitializerWorker = new Worker(new URL('./workers/initialization.worker.ts', import.meta.url), {
            type: 'module',
          });
          this.appInitializerWorker?.postMessage({ filters });
          this.appInitializerWorker.onmessage = ({ data }) => {
            const { customerFilters, materialFilters } = data;
            this.store.dispatch(storeFilters({ customerFilters: customerFilters, materialFilters: materialFilters }));
            this.appLoadingStatus.set(LOADING_STATUS.LOADED);
            this.showMain();
            this.appInitializerWorker?.terminate();
            this.appInitializerWorker = undefined;
          };

          const avatarBase64 = azureProfileImage
            ? 'data:image/jpeg;base64,' + this.arrayBufferToBase64(azureProfileImage as ArrayBuffer)
            : null;
          this.store.dispatch(
            setProfile({
              profile: {
                fullName: (azureProfile as any)!.displayName,
                emailAddress: (azureProfile as any)!.mail,
                avatar: avatarBase64,
              },
            })
          );
        },
        (error: any) => {
          console.log(error);
          this.error = error.error.message;
          this.appLoadingStatus.set(LOADING_STATUS.ERROR);
          this.appLoaderState.update((state: any) => ({
            ...state,
            hasApiFailed: true,
          }));
        }
      );
    }
  }

  msalInit() {
    this.authService.handleRedirectObservable().subscribe();

    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$.subscribe((result: any) => {
      if (result.eventType === EventType.ACCOUNT_ADDED || result.eventType === EventType.ACCOUNT_REMOVED) {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
          // this.setLoginDisplay();
        }
      } else if (result.eventType === EventType.ACQUIRE_TOKEN_SUCCESS || result.eventType === EventType.LOGIN_SUCCESS) {
        this.sessionStorageService.setItem('accessToken', result.payload?.accessToken);
        this.sessionStorageService.setItem('idToken', result.payload?.idToken);
      } else if (result.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
        // this.loginRedirect();
      }
    });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe((result) => {
        const migrationFlag = 'msalMigrationDone';
        if (localStorage.getItem(migrationFlag) !== 'v2.1') {
          localStorage.clear();
          sessionStorage.clear();
          localStorage.setItem(migrationFlag, 'v2.1');
          setTimeout(() => {
            this.authService.instance.logoutRedirect();
          }, 1000);
        }

        this.checkAndSetActiveAccount();
        const account = this.authService.instance.getActiveAccount();
        if (account) {
          this.authService.acquireTokenSilent({ scopes: ['user.read'], account }).subscribe({
            next: (res) => {
              this.sessionStorageService.setItem('accessToken', res.accessToken);
              this.sessionStorageService.setItem('idToken', res.idToken);
            },
            error: (err) => {
              console.warn('Silent token acquisition failed', err);
              // this.authService.loginRedirect(); // Optional fallback
              this.authService.instance.logoutRedirect();
            },
          });
        }
      });
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  mainClasses = computed(() => ({
    fadeIn: this.appMainContainState().fadeInMain,
    hidden: this.appMainContainState().hiddenMain,
  }));

  appLoaderClasses = computed(() => ({
    fadeOut: this.appLoaderState().fadeOut,
    hidden: this.appLoaderState().hidden,
  }));

  showMain() {
    // Instantly hide loader and show main content
    this.appLoaderState.update((state: any) => ({
      ...state,
      fadeOut: true,
      hidden: true,
    }));

    this.appMainContainState.update((state: any) => ({
      ...state,
      hidden: true,
      fadeIn: true,
    }));
  }

  retryApiCalls() {
    this.appLoaderState.set({ fadeOut: false, hidden: false, hasApiFailed: false });
    this.eventBusService.emit('RETRY', 'RETRY');
    this.initializeAppData();
  }
}
